/**
 * AppShell — Main layout component replacing PageLayout.
 * Desktop: één menukolom (SidebarNav, 240px; sectie klapt open onder het actieve
 * hoofditem) + topbar + content. Er is geen aparte sectiekolom meer (menu-ronde 2).
 * Mobile: hidden sidebar with hamburger, topbar + content area.
 */
import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';

import SidebarNav, { VisibilityToggles } from './SidebarNav';
import PlanningTabs from './PlanningTabs';
import { useSectieMenu } from './SecondaryNav';
import { useVisibilityFilters } from '../contexts/VisibilityFiltersContext';
import NotificationBell from './NotificationBell';
import useNotifications from '../hooks/useNotifications';
import { useUserRole } from '../contexts/UserRoleContext';
import {
  getUserMenuItems, isFullScreenPath, getFocusKey, getKruimelpad,
  getSidebarItems, isSidebarItemActive, isPlanningTabPath,
} from '../config/navigation';
import { versionInfo } from '../config/versionInfo';
import { TOPBAR_HOOGTE, TESTBALK_TOP } from '../utils/testbalk';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Ronde 4: 40 i.p.v. 44 — op de meeste schermen staat er links niets (geen dubbel
// kruimelpad meer), dan hoort de balk niet meer ruimte te nemen dan zoeken + avatar.
const TOPBAR_HEIGHT = TOPBAR_HOOGTE;

export default function AppShell({ onSearchClick, children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { instance } = useMsal();
  const { permissions } = useUserRole();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const activeAccount = instance.getActiveAccount();
  const userName = activeAccount?.name || 'Gebruiker';
  const userEmail = activeAccount?.username || '';
  const { notifications, dismiss, dismissAll } = useNotifications(userEmail, userName);
  // E-mail mee, anders valt de allowlist (Sync-check) altijd weg
  const userMenuItems = getUserMenuItems(permissions, userEmail);

  // Kruimelpad "Productie › Callsheets" alleen waar het iets toevoegt: niet op hubs, niet
  // op de planningsweergaven (daar staat de tabbalk) en niet als het menu het scherm al
  // als actief item toont (hoofditem of uitgeklapt submenu).
  const visibility = useVisibilityFilters();
  const hoofdItems = getSidebarItems(permissions, userEmail, visibility).primary;
  const actieveSectie = hoofdItems.find((i) => isSidebarItemActive(i, location.pathname))?.section;
  const sectieMenu = useSectieMenu(actieveSectie);
  const inMenu = [
    ...hoofdItems.map((i) => i.path),
    ...(sectieMenu?.items || []).map((i) => i.path),
  ];
  const kruimels = getKruimelpad(location.pathname, inMenu);
  const planningTabs = isPlanningTabPath(location.pathname);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    handleMenuClose();
    instance.logoutRedirect();
  };

  // Layout-metadata komt centraal uit config/navigation.js — géén eigen lijsten hier.
  // Full-screen editors verbergen de zijbalk (alleen via de hamburger bereikbaar).
  const isFullWidthPage = isFullScreenPath(location.pathname);
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
  const sneltoets = isMac ? '⌘K' : 'Ctrl+K';
  // Focusschermen (Vandaag, Planner To-Do, Dagstart, overleg-sessie): hoofdmenu klapt in
  const focusKey = getFocusKey(location.pathname);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar — persistent on desktop (hidden for full-width pages), drawer on mobile */}
      <SidebarNav
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        hidePersistent={isFullWidthPage}
        focusKey={focusKey}
      />

      {/* Main area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: '100vh',
        }}
      >
        {/* Topbar */}
        <AppBar
          position="sticky"
          elevation={0}
          color="inherit"
          sx={{
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: 'none',
            boxShadow: '0 10px 30px rgba(45,51,53,0.04)',
            zIndex: theme.zIndex.appBar,
            // Plakt onder de TEST-balk, niet erachter (productie: 0px)
            top: TESTBALK_TOP,
          }}
        >
          <Toolbar
            sx={{
              minHeight: `${TOPBAR_HEIGHT}px !important`,
              maxHeight: `${TOPBAR_HEIGHT}px !important`,
              px: { xs: 1.5, sm: 2 },
              gap: 0.5,
            }}
          >
            {/* Left: hamburger (mobile) + page title */}
            <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', gap: 0.75, flex: 1, minWidth: 0 }}>
              {(isMobile || isFullWidthPage) && (
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  size="small"
                  aria-label="Hoofdmenu openen"
                  aria-expanded={mobileOpen}
                  sx={{ color: 'text.disabled' }}
                >
                  <MenuIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              {/* Planning: de vier weergaven als tabs, op alle vier op dezelfde plek */}
              {planningTabs && <PlanningTabs />}
              {/* Kruimelpad; op hubs leeg — de paginakop is daar leidend */}
              {!planningTabs && kruimels.length > 0 && (
                <Box component="nav" aria-label="Kruimelpad"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, fontSize: '0.8rem', letterSpacing: '-0.01em' }}>
                  {kruimels.map((k, i) => (
                    <React.Fragment key={`${k.label}-${i}`}>
                      {i > 0 && <Box component="span" aria-hidden sx={{ color: '#c4c8ca' }}>›</Box>}
                      {k.path ? (
                        <Box component={RouterLink} to={k.path}
                          sx={{ color: '#9aa1a6', textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { color: '#635983' } }}>
                          {k.label}
                        </Box>
                      ) : (
                        <Typography noWrap component="span" aria-current="page"
                          sx={{ fontSize: 'inherit', fontWeight: 500, color: 'text.secondary', letterSpacing: 'inherit' }}>
                          {k.label}
                        </Typography>
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>

            {/* Right: search + notifications + profile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {/* Zoeken: de enige ingang (de zijbalk heeft geen zoekveld meer) — dus
                  goed zichtbaar, met de sneltoets erbij op desktop. */}
              <Tooltip title={`Zoeken naar schermen, projecten en mensen (${sneltoets})`}>
                <Box
                  component="button"
                  type="button"
                  onClick={onSearchClick}
                  aria-label="Zoeken"
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    height: 30, px: { xs: 0.75, md: 1.25 }, mr: 0.5,
                    border: { xs: 0, md: '1px solid rgba(45,51,53,0.1)' },
                    borderRadius: '0.5rem', bgcolor: { xs: 'transparent', md: '#fff' },
                    color: '#556071', cursor: 'pointer', font: 'inherit',
                    '&:hover': { borderColor: 'rgba(99,89,131,0.35)', color: '#635983' },
                  }}
                >
                  <SearchIcon sx={{ fontSize: 18 }} />
                  <Typography component="span" sx={{ display: { xs: 'none', md: 'inline' }, fontSize: '0.78rem', color: 'inherit' }}>
                    Zoeken
                  </Typography>
                  <Typography component="span" sx={{
                    display: { xs: 'none', md: 'inline' }, fontSize: '0.62rem', fontFamily: 'monospace',
                    color: '#9aa1a6', border: '1px solid rgba(45,51,53,0.1)', borderRadius: '4px', px: 0.5, lineHeight: 1.5,
                  }}>
                    {sneltoets}
                  </Typography>
                </Box>
              </Tooltip>

              {/* Notificatiecentrum tijdelijk verborgen — komt later terug */}
              {false && (
                <NotificationBell
                  notifications={notifications}
                  onDismiss={dismiss}
                  onDismissAll={dismissAll}
                />
              )}

              <Tooltip title={userName}>
                <IconButton onClick={handleMenuOpen} size="small" sx={{ ml: 0.25 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      bgcolor: '#635983',
                      border: '2px solid rgba(99,89,131,0.2)',
                    }}
                  >
                    {getInitials(userName)}
                  </Avatar>
                </IconButton>
              </Tooltip>

              {/* Profile menu */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    borderRadius: 2,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                    {userName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                    {userEmail}
                  </Typography>
                </Box>

                {userMenuItems.map((item) => (
                  <MenuItem
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    onClick={handleMenuClose}
                    sx={{ py: 1, fontSize: '0.82rem' }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <item.Icon sx={{ fontSize: 17, color: 'text.secondary' }} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: '0.82rem' }}>{item.label}</ListItemText>
                  </MenuItem>
                ))}

                {/* BETA/PLANNED-schakelaars: een voorkeur, geen bestemming → uit de zijbalk, hierheen */}
                <Divider />
                <VisibilityToggles />

                <Divider />

                <MenuItem onClick={handleLogout} sx={{ py: 1, fontSize: '0.82rem', color: 'text.secondary' }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LogoutIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: '0.82rem' }}>Uitloggen</ListItemText>
                </MenuItem>

                {/* Versie: klein onderaan (was de regel in de zijbalkkop); hover = laatste wijzigingen */}
                <Tooltip placement="left" enterDelay={150}
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Laatste wijzigingen</Typography>
                      {versionInfo.changes.map((c, i) => (
                        <Typography key={i} variant="caption" display="block">{c}</Typography>
                      ))}
                    </Box>
                  }>
                  <Typography sx={{ px: 2, pt: 0.5, pb: 1, fontSize: '0.62rem', fontFamily: 'monospace', color: 'text.disabled', cursor: 'help' }}>
                    v{versionInfo.version} · {versionInfo.releaseDate}
                  </Typography>
                </Tooltip>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            minHeight: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
            minWidth: 0,
          }}
        >
          <Box
            component="main"
            sx={{
              flex: 1,
              overflow: 'auto',
              minWidth: 0,
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
